import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import Markdown from 'markdown-to-jsx';
import { useUser } from '@/hooks/useUser';
import { PageWrapper } from '@/layout/components/PageWrapper';
import { PageHeader } from '@/layout/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Save, Eye, EyeOff, History, UserRound, PenLine, MessageSquareText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
    useGetDocumentationQuery,
    useCreateDocumentationMutation,
    useUpdateDocumentationMutation,
} from '@/store/documentationApi';
import { ChangeSummaryDialog } from './components/ChangeSummaryDialog';
import { ChangeHistoryPanel } from '@/pages/documentation/components/ChangeHistoryPanel';
import { AiGenerateDialog } from './components/AiGenerateDialog';

// ──────────────────────────────────────────────
// Markdown preview styling wrapper
// ──────────────────────────────────────────────
function MarkdownPreview({ content }: { content: string }) {
    return (
        <div
            dir="rtl"
            className="
                prose prose-base max-w-none h-full overflow-y-auto px-6 py-4 font-sans
                prose-headings:font-semibold prose-headings:text-foreground
                prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-h3:font-bold prose-h3:border-b prose-h3:border-border prose-h3:pb-1.5
                prose-p:leading-relaxed prose-li:leading-relaxed
                prose-a:text-blue-600 prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none
                prose-pre:bg-muted prose-pre:p-3 prose-pre:rounded-lg prose-pre:border prose-pre:border-border
                prose-blockquote:border-r-4 prose-blockquote:border-primary prose-blockquote:pr-4 prose-blockquote:italic prose-blockquote:text-muted-foreground
                prose-hr:border-border
                prose-table:text-sm prose-table:w-full prose-table:border-collapse prose-table:overflow-hidden prose-table:rounded-md
                prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-3 prose-th:py-2 prose-th:text-right prose-th:font-bold
                prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2 prose-td:text-right prose-td:align-top
                [&_tbody_tr:nth-child(even)]:bg-muted/30
                [&_pre]:direction-ltr [&_pre]:text-left [&_pre]:[unicode-bidi:isolate]
                [&_code]:direction-ltr [&_code]:text-left [&_code]:[unicode-bidi:isolate]
                [&_table]:direction-rtl
            "
        >
            {content.trim() ? (
                <Markdown
                    options={{
                        disableParsingRawHTML: true,
                        overrides: {
                            h1: {
                                props: {
                                    dir: 'rtl',
                                    style: { fontWeight: 800, textAlign: 'right' },
                                },
                            },
                            h2: {
                                props: {
                                    dir: 'rtl',
                                    style: { fontWeight: 800, textAlign: 'right' },
                                },
                            },
                            h3: {
                                props: {
                                    dir: 'rtl',
                                    style: { fontWeight: 800, textAlign: 'right' },
                                },
                            },
                            table: {
                                props: {
                                    dir: 'rtl',
                                    className: 'documentation-markdown-table',
                                    style: {
                                        width: '100%',
                                        direction: 'rtl',
                                        tableLayout: 'auto',
                                    },
                                },
                            },
                            th: {
                                props: {
                                    dir: 'rtl',
                                    style: {
                                        direction: 'rtl',
                                        textAlign: 'right',
                                        fontWeight: 800,
                                        border: '1px solid #94a3b8',
                                        backgroundColor: '#e2e8f0',
                                        padding: '0.625rem 0.75rem',
                                        verticalAlign: 'top',
                                    },
                                },
                            },
                            td: {
                                props: {
                                    dir: 'rtl',
                                    style: {
                                        direction: 'rtl',
                                        textAlign: 'right',
                                        border: '1px solid #94a3b8',
                                        padding: '0.625rem 0.75rem',
                                        verticalAlign: 'top',
                                    },
                                },
                            },
                            pre: {
                                props: {
                                    dir: 'ltr',
                                    style: { direction: 'ltr', textAlign: 'left', unicodeBidi: 'isolate' },
                                },
                            },
                            code: {
                                props: {
                                    dir: 'ltr',
                                    style: { direction: 'ltr', textAlign: 'left', unicodeBidi: 'isolate' },
                                },
                            },
                        },
                    }}
                >
                    {content}
                </Markdown>
            ) : (
                <p className="text-muted-foreground italic">תצוגה מקדימה תופיע כאן…</p>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────
export default function DocumentationEditPage() {
    const { user } = useUser();
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isNew = !id;
    const numericId = id ? parseInt(id, 10) : 0;

    const { data: existing, isLoading } = useGetDocumentationQuery(numericId, {
        skip: isNew || !user?.is_superuser,
    });

    const [createDoc, { isLoading: isCreating }] = useCreateDocumentationMutation();
    const [updateDoc, { isLoading: isUpdating }] = useUpdateDocumentationMutation();

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [authorName, setAuthorName] = useState('');
    const [showPreview, setShowPreview] = useState(true);
    const [showHistory, setShowHistory] = useState(false);

    // dialog state after save
    const [changeSummaryOpen, setChangeSummaryOpen] = useState(false);
    const [pendingDiff, setPendingDiff] = useState('');

    // AI generation dialog state
    const [aiDialogOpen, setAiDialogOpen] = useState(false);

    // Derive editor display name from logged-in user
    const editorName = user ? user.full_name || user.username : '';

    // Initialise fields once data is loaded
    useEffect(() => {
        if (existing) {
            setTitle(existing.title);
            setContent(existing.content);
            setAuthorName(existing.author_name);
        }
    }, [existing]);

    // Pre-fill author with logged-in user on new docs
    useEffect(() => {
        if (isNew && editorName && !authorName) {
            setAuthorName(editorName);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNew, editorName]);

    const isSaving = isCreating || isUpdating;

    const handleSave = useCallback(async () => {
        if (!title.trim()) {
            toast.error('יש להזין כותרת');
            return;
        }
        try {
            if (isNew) {
                const created = await createDoc({
                    title: title.trim(),
                    content,
                    author_name: authorName,
                }).unwrap();
                toast.success('נוצר בהצלחה');
                navigate(`/admin/documentation/${created.id}/edit/`, { replace: true });
            } else {
                const result = await updateDoc({
                    id: numericId,
                    title: title.trim(),
                    content,
                    author_name: authorName,
                    last_editor_name: editorName,
                }).unwrap();
                toast.success('נשמר בהצלחה');
                if (result.has_changes) {
                    setPendingDiff(result.diff);
                    setChangeSummaryOpen(true);
                }
            }
        } catch {
            toast.error('שגיאה בשמירה');
        }
    }, [title, content, authorName, editorName, isNew, numericId, createDoc, updateDoc, navigate]);

    // Ctrl/Cmd+S shortcut
    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                handleSave();
            }
        }
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [handleSave]);

    if (!user?.is_superuser) {
        return (
            <PageWrapper>
                <div className="text-center py-20 text-gray-500">
                    <p className="text-xl">גישה מוגבלת לסופר יוזר בלבד</p>
                </div>
            </PageWrapper>
        );
    }

    if (!isNew && isLoading) {
        return (
            <PageWrapper>
                <div className="flex justify-center py-20">
                    <Spinner />
                </div>
            </PageWrapper>
        );
    }

    return (
        <>
        <div className="flex flex-col h-[calc(100vh-64px)] bg-muted/10" dir="rtl">
            {/* ── Top bar ── */}
            <div className="flex flex-wrap items-center gap-3 px-4 sm:px-9 py-3 border-b bg-background shadow-sm shrink-0">
                <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/admin/documentation/')}>
                    <ArrowRight className="h-4 w-4" />
                </Button>

                <PageHeader title={isNew ? 'רשומת תיעוד חדשה' : 'עריכת תיעוד'} />

                <div className="flex-1" />

                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAiDialogOpen(true)}
                        className="gap-1.5"
                    >
                        <Sparkles className="h-4 w-4" />
                        צור/ערוך עם AI
                    </Button>

                    {!isNew && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowHistory((v) => !v)}
                            className="gap-1.5"
                        >
                            <History className="h-4 w-4" />
                            היסטוריה
                        </Button>
                    )}

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPreview((v) => !v)}
                        className="gap-1.5"
                    >
                        {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        {showPreview ? 'הסתר תצוגה' : 'הצג תצוגה'}
                    </Button>

                    <Button size="sm" onClick={handleSave} disabled={isSaving} className="gap-1.5">
                        {isSaving ? <Spinner className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                        שמור
                        <span className="hidden sm:inline text-primary-foreground/70 text-xs font-normal">(Ctrl+S)</span>
                    </Button>
                </div>
            </div>

            {/* ── Title + meta card ── */}
            <div className="px-4 sm:px-9 pt-4 pb-3 border-b bg-background shrink-0 space-y-3">
                <Input
                    placeholder="כותרת…"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="text-xl font-semibold h-auto border-0 border-b-2 border-transparent focus-visible:border-primary rounded-none shadow-none focus-visible:ring-0 px-0 py-1.5 transition-colors"
                    dir="rtl"
                />

                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 rounded-full border bg-muted/30 px-3 py-1 text-sm">
                        <UserRound className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <Input
                            value={authorName}
                            onChange={(e) => setAuthorName(e.target.value)}
                            placeholder="שם יוצר / AI"
                            className="h-5 text-sm border-0 shadow-none focus-visible:ring-0 px-0 w-36"
                            dir="rtl"
                        />
                    </div>

                    {!isNew && existing?.last_editor_name && (
                        <Badge variant="secondary" className="gap-1.5 font-normal py-1 px-3 rounded-full">
                            <PenLine className="h-3.5 w-3.5" />
                            עורך אחרון: <span className="font-medium">{existing.last_editor_name}</span>
                        </Badge>
                    )}

                    {!isNew && existing?.last_edit_summary && (
                        <Badge
                            variant="outline"
                            className="gap-1.5 font-normal py-1 px-3 rounded-full max-w-xs"
                            title={existing.last_edit_summary}
                        >
                            <MessageSquareText className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{existing.last_edit_summary}</span>
                        </Badge>
                    )}
                </div>
            </div>

            {/* ── Main area: history panel + editor + preview ── */}
            <div className="flex flex-1 overflow-hidden gap-3 p-3">
                {/* History sidebar */}
                {showHistory && !isNew && (
                    <ChangeHistoryPanel
                        docId={numericId}
                        onClose={() => setShowHistory(false)}
                    />
                )}

                {/* Editor */}
                <div
                    className={`flex flex-col rounded-xl border bg-background shadow-sm ${showPreview ? 'w-1/2' : 'w-full'} overflow-hidden`}
                >
                    <div className="px-4 py-2 text-xs font-semibold text-muted-foreground border-b bg-muted/30 shrink-0 tracking-wide">
                        עורך Markdown
                    </div>
                    <textarea
                        className="flex-1 w-full resize-none font-sans text-[15px] p-4 bg-background outline-none leading-loose placeholder:text-muted-foreground/60"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="כתוב כאן בפורמט Markdown…"
                        dir="rtl"
                        spellCheck={true}
                        lang="he"
                    />
                </div>

                {/* Preview */}
                {showPreview && (
                    <div className="flex flex-col w-1/2 rounded-xl border bg-background shadow-sm overflow-hidden">
                        <div className="px-4 py-2 text-xs font-semibold text-muted-foreground border-b bg-muted/30 shrink-0 tracking-wide">
                            תצוגה מקדימה
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <MarkdownPreview content={content} />
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* צור עדכון עם AI — ממלא מראש כותרת/תוכן בעורך, ללא שמירה אוטומטית */}
        <AiGenerateDialog
            open={aiDialogOpen}
            onOpenChange={setAiDialogOpen}
            onApply={(aiTitle, aiContent) => {
                if (aiTitle) setTitle(aiTitle);
                setContent(aiContent);
            }}
            existingTitle={isNew ? undefined : title}
            existingContent={isNew ? undefined : content}
        />

        {/* ── Change summary dialog ── */}
        <ChangeSummaryDialog
            open={changeSummaryOpen}
            onOpenChange={setChangeSummaryOpen}
            docId={numericId}
            diff={pendingDiff}
            editorName={editorName}
        />
        </>
    );
}
